import { type Dispatch, useId } from 'react';
import { useTranslation } from 'react-i18next';

import Info from '@/assets/icons/info.svg?component';
import Warning from '@/assets/icons/warning.svg?component';
import { Alert } from '@/components/atoms/Alert';
import FormField from '@/components/atoms/FormField';
import Select from '@/components/atoms/Select';
import { SvgIcon } from '@/components/atoms/SvgIcon/SvgIcon';
import type {
    OperateWizardAction,
    WizardDestination,
} from '@/routes/Internal/routes/Client/FishGroupsOverview/actions/operations/operateWizardReducer';

import {
    absorbedNameForOccupied,
    type EmptyCandidate,
    type OccupiedCandidate,
} from './targetCandidates';

interface Props {
    initialSourceId: number;
    mergeTargetDestination: WizardDestination | null;
    samTargetMode: 'occupied' | 'empty' | null;
    selectedCageId: number | null;
    emptyCandidates: EmptyCandidate[];
    occupiedCandidates: OccupiedCandidate[];
    dispatch: Dispatch<OperateWizardAction>;
}

export function TargetCageField(props: Props) {
    const {
        initialSourceId,
        mergeTargetDestination,
        samTargetMode,
        selectedCageId,
        emptyCandidates,
        occupiedCandidates,
        dispatch,
    } = props;
    const { t } = useTranslation('fishGroups');
    const samTargetCageId = useId();

    const handleSelectCage = (rawValue: string) => {
        if (rawValue === '') {
            dispatch({ type: 'clearTarget' });
            return;
        }
        const cageId = Number(rawValue);
        const occupied = occupiedCandidates.find((c) => c.cageId === cageId);
        if (occupied) {
            dispatch({
                type: 'setOccupiedTarget',
                cageId: occupied.cageId,
                occupantFishGroupId: occupied.occupantId,
                occupantName: occupied.occupantName,
                occupantCageId: occupied.cageId,
            });
            return;
        }
        const empty = emptyCandidates.find((c) => c.cageId === cageId);
        if (empty) {
            dispatch({ type: 'setEmptyTarget', cageId: empty.cageId });
        }
    };

    return (
        <FormField
            size="sm"
            htmlFor={samTargetCageId}
            label={t('actions.operate.splitAndMerge.target.cageLabel')}>
            <Select
                id={samTargetCageId}
                value={selectedCageId == null ? '' : String(selectedCageId)}
                onChange={(e) => handleSelectCage(e.target.value)}>
                <option value="">
                    {t('actions.operate.splitAndMerge.target.cagePlaceholder')}
                </option>
                {emptyCandidates.length > 0 && (
                    <optgroup label={t('actions.operate.splitAndMerge.target.optgroup.empty')}>
                        {emptyCandidates.map((c) => (
                            <option key={`empty-${c.cageId}`} value={c.cageId}>
                                {t('actions.operate.splitAndMerge.target.cageOption.empty', {
                                    cageName: c.cageName,
                                })}
                            </option>
                        ))}
                    </optgroup>
                )}
                {occupiedCandidates.length > 0 && (
                    <optgroup label={t('actions.operate.splitAndMerge.target.optgroup.occupied')}>
                        {occupiedCandidates.map((c) => (
                            <option key={`occupied-${c.cageId}`} value={c.cageId}>
                                {t('actions.operate.splitAndMerge.target.cageOption.occupied', {
                                    cageName: c.cageName,
                                    fishGroupName: c.occupantName,
                                })}
                            </option>
                        ))}
                    </optgroup>
                )}
            </Select>
            {mergeTargetDestination && samTargetMode === 'occupied' && (
                <Alert
                    tone="warning"
                    size="small"
                    live="polite"
                    icon={<SvgIcon icon={Warning} size="small" color="currentColor" />}>
                    {t('actions.operate.splitAndMerge.target.absorbHint', {
                        fishGroupName: absorbedNameForOccupied(
                            mergeTargetDestination,
                            initialSourceId,
                            occupiedCandidates
                        ),
                    })}
                </Alert>
            )}
            {mergeTargetDestination && samTargetMode === 'empty' && (
                <Alert
                    tone="info"
                    size="small"
                    icon={<SvgIcon icon={Info} size="small" color="currentColor" />}>
                    {t('actions.operate.splitAndMerge.target.emptyHint')}
                </Alert>
            )}
        </FormField>
    );
}
